import React, { useEffect, useRef, useCallback, useState } from "react";
import PhoneInput, {
  isValidPhoneNumber,
  parsePhoneNumber,
} from "react-phone-number-input";
import { getCountryCodeFromIpInfo } from "@greythr/shared-utils/ipinfo-country";
import { Box, Text } from "theme-ui";
import { colors, typography } from "../../styles/theme";
import "react-phone-number-input/style.css";

const PhoneInputField = ({
  label,
  value,
  onChange,
  onValidationChange,
  onCountryChange,
  error,
  disabled,
  placeholder,
  required = false,
}) => {
  const [defaultCountry, setDefaultCountry] = useState("IN");

  useEffect(() => {
    const getDefaultCountry = async () => {
      const country = await getCountryCodeFromIpInfo();
      if (country) {
        setDefaultCountry(country);
      }
    };
    getDefaultCountry();
  }, []);

  const lastReportedCountryRef = useRef();
  const notifyCountryChange = useCallback(
    (country) => {
      if (!onCountryChange || !country) return;
      if (lastReportedCountryRef.current === country) return;
      lastReportedCountryRef.current = country;
      onCountryChange(country);
    },
    [onCountryChange],
  );

  useEffect(() => {
    if (!value) return;
    try {
      const parsed = parsePhoneNumber(value);
      if (parsed?.country) notifyCountryChange(parsed.country);
    } catch {
      // ignore parse errors; keep user-selected country
    }
  }, [value, notifyCountryChange]);

  const handleChange = (val) => {
    onChange(val);
    if (onValidationChange) {
      if (!val) {
        onValidationChange(null);
      } else {
        onValidationChange(
          isValidPhoneNumber(val) ? null : "Please enter a valid phone number",
        );
      }
    }
  };

  return (
    <Box sx={{ mb: "0.75rem" }}>
      {label && (
        <Box
          as="label"
          sx={{
            ...typography.textMd400,
            display: "block",
            mb: "0.5rem",
          }}
        >
          {label}
          {required && <Text sx={{ color: colors.errorMain }}> *</Text>}
        </Box>
      )}
      <PhoneInput
        international
        countryCallingCodeEditable={false}
        defaultCountry={defaultCountry}
        value={value}
        onChange={handleChange}
        onCountryChange={notifyCountryChange}
        disabled={disabled}
        placeholder={placeholder}
        className={`demo-phone-input${disabled ? " demo-phone-input--disabled" : ""}`}
        numberInputProps={{
          className: "demo-phone-number-input",
        }}
        countrySelectProps={{
          className: "demo-phone-country-select",
        }}
      />
      {error && (
        <Box
          sx={{
            ...typography.textSm,
            color: colors.errorMain,
            mt: "0.25rem",
          }}
        >
          {error}
        </Box>
      )}
      <style>{`
        .demo-phone-input {
          display: flex;
          width: 100%;
          border: 1px solid ${colors.neutral300};
          border-radius: 0.625rem;
          overflow: hidden;
          --PhoneInput-color--focus: ${colors.primaryMain};
          --PhoneInputCountrySelectArrow-color: ${colors.primaryMain};
          --PhoneInputCountrySelectArrow-color--focus: ${colors.primaryMain};
          --PhoneInputCountrySelectArrow-opacity: 1;
          --PhoneInputCountrySelectArrow-width: 0.625rem;
        }
        .demo-phone-input:focus-within {
          outline: none;
          border-color: ${colors.primaryMain};
          box-shadow: 0 0 0 1px ${colors.primaryMain};
        }
        .demo-phone-input .PhoneInputCountrySelect:focus + .PhoneInputCountryIcon--border {
          box-shadow: none !important;
        }
        .demo-phone-input .PhoneInputCountry {
          padding: 0.5rem;
          background: ${colors.neutral200};
          min-width: 5rem;
          flex-shrink: 0;
          box-shadow: none;
        }
        .demo-phone-input .PhoneInputCountryIcon {
          width: 1.25rem;
          height: 0.938rem;
        }
        .demo-phone-input .PhoneInputCountrySelectArrow {
          border-color: ${colors.primaryMain};
          color: ${colors.primaryMain};
          flex-shrink: 0;
         margin-left: 1rem;
        }
        .demo-phone-input .PhoneInputCountrySelect:focus + .PhoneInputCountryIcon + .PhoneInputCountrySelectArrow {
          border-color: ${colors.primaryMain};
          color: ${colors.primaryMain};
           margin-left: 1rem;
        }
        .demo-phone-number-input {
          flex: 1;
          min-width: 0;
          padding: 0.625rem 0.875rem;
          border: none;
          font-size: 1rem;
          line-height: 1.5;

        }
        .demo-phone-number-input::placeholder {
          color: ${colors.neutral700};
        }
        .demo-phone-number-input:focus {
          outline: none;
        }
        .demo-phone-input--disabled {
          opacity: 0.9;
          pointer-events: none;
        }
      `}</style>
    </Box>
  );
};

export default PhoneInputField;
export { isValidPhoneNumber };
